import React from 'react';
import useMapReveal from 'screens/Property/hooks/useMapReveal';
import LoadingSpinner from 'components/LoadingSpinner';
import MapLoadingPlaceholder from 'screens/Property/components/MapLoadingPlaceholder';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import Breadcrumb from 'components/App/AppShell/components/Breadcrumb';
import ClickableDiv from 'components/ClickableDiv';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import FormNavGuard from 'components/FormNavGuard';
import { paths } from 'routes/paths';
import PropertyDetailsToast from 'screens/Property/PropertiesLanding/components/PropertyDetailsToast';
import PropertyMapWrapper from '../PropertyLandingMap/PropertyMapWrapper';
import FieldCard from './components/FieldCard';
import PropertySelector from './components/PropertySelector';
import { CREATION_STATES } from './helpers.js/constants';
import schema from './helpers.js/schema';
import usePropertyFeatures from './hooks/usePropertyFeatures';

const LEAVING_CROP_ZONE =
  'Are you sure you do not want to create a cropzone also?';

const PropertyCreateScreen = () => {
  const { fieldId, action } = useParams();

  const isCreating = action === 'create';

  const history = useHistory();
  const { state } = useLocation();

  const {
    redirect,
    setRedirect,
    formValues,
    creationState,
    newFieldFeatures,
    openModal,
    handleModalCancel,
    handleModalClose,
    handleModalConfirm,
    isBackArrowClicked,
    handleBackArrowCancel,
    chkNavGuardDisabled,
    navGuardDisabled,
    handleSaveAndCreateCropZone,
    handleSaveAndRedirectToProperties,
    handleSaveAndDrawAgain,
    handleCancelBackButton,
    setChkNavGuardDisabled,
    setIsBackArrowClicked,
    handleCreateFarm,
    farms,
    fieldToEdit,
    geoJSONCollection,
    canShowMap,
    isFormLoading,
    shapeIds,
    handleFeaturesChange,
    handleFeaturesSelect,
    fieldZoom,
    setZoom,
    isToastVisible,
    setIsToastVisible,
    saveProperty,
    fieldCentroidCoordinates
  } = usePropertyFeatures(fieldId, action, history, state);
  // Only once the map is on screen: while a field loads the collection is
  // still empty, which is not the same as having nothing to fit. A new field
  // is revealed by the map once its initial view is set.
  const { revealMap, mapRevealClassName, showPlaceholder } = useMapReveal(
    canShowMap && !!fieldId && !geoJSONCollection?.features?.length,
    !canShowMap
  );
  return (
    <>
      {redirect && <Redirect push to={`${redirect}`} />}
      <Breadcrumb disabled hideCropSeasonDropdown={!isCreating}>
        <Breadcrumb.Item
          title="Property"
          value="All Properties"
          to={paths.properties}
        />
        <Breadcrumb.Item
          title="Field"
          value={fieldId ? 'Edit Field' : 'New Field'}
          isLast
        />
      </Breadcrumb>
      <div className="h-full" data-testid="property-create-screen">
        <Formik
          initialValues={formValues}
          enableReinitialize
          onSubmit={(values, { setSubmitting }) => {
            saveProperty(values, !!fieldId, setSubmitting);
          }}
          validationSchema={schema}
          validateOnChange
          validateOnMount
        >
          {({ values, setSubmitting, isValid, isSubmitting }) => {
            const canSave =
              creationState !== CREATION_STATES.SAVING &&
              isValid &&
              newFieldFeatures?.length > 0;

            return (
              <>
                <ConfirmationModal
                  open={openModal}
                  handleCancel={() => handleModalCancel(values, setSubmitting)}
                  handleClose={() => handleModalClose(setSubmitting)}
                  handleConfirm={() =>
                    handleModalConfirm(values, setSubmitting)
                  }
                >
                  Are you sure you want to create just a field and not a crop
                  zone with it?
                </ConfirmationModal>
                <ConfirmationModal
                  open={isBackArrowClicked}
                  handleCancel={handleBackArrowCancel}
                  handleConfirm={() => setRedirect(paths.properties)}
                  handleClose={handleBackArrowCancel}
                >
                  Are you sure you want to leave this page?
                </ConfirmationModal>
                <FormNavGuard
                  customText={!fieldId ? null : LEAVING_CROP_ZONE}
                  disabled={chkNavGuardDisabled || navGuardDisabled.current}
                  alwaysShow={!!fieldId}
                />
                <div className="bg-white flex w-full h-full">
                  <div
                    data-testid="property-create-left-panel"
                    className="flex flex-col w-1/3 min-w-0 h-auto m-4 relative"
                  >
                    {isFormLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <LoadingSpinner
                          message=""
                          size={50}
                          label="Loading field"
                        />
                      </div>
                    ) : (
                      <ClickableDiv className="flex-1 h-full">
                        <FieldCard
                          features={newFieldFeatures}
                          saveAndCreateCZ={() =>
                            handleSaveAndCreateCropZone(values, setSubmitting)
                          }
                          saveAndDirectToProperties={() =>
                            handleSaveAndRedirectToProperties(
                              values,
                              setSubmitting
                            )
                          }
                          saveAndDrawAgain={() =>
                            handleSaveAndDrawAgain(values, setSubmitting)
                          }
                          canSave={canSave}
                          cancelBackButton={handleCancelBackButton}
                          label={action}
                          fieldId={fieldId}
                          setChkNavGuardDisabled={setChkNavGuardDisabled}
                          setIsBackArrowClicked={setIsBackArrowClicked}
                          chkSubmitStatus={isSubmitting}
                        >
                          <PropertySelector
                            onCreate={handleCreateFarm}
                            items={farms}
                            isDisabled={!isCreating}
                            selectedValue={fieldToEdit?.propertyId}
                            fieldsAndAreasGeoJSONCollection={geoJSONCollection}
                          />
                        </FieldCard>
                      </ClickableDiv>
                    )}
                  </div>
                  <div
                    data-testid="property-create-right-panel"
                    className="relative w-full"
                    id="map-wrapper-container"
                  >
                    {/* One loading look from the first fetch to the fit: the
                        placeholder is on while the map cannot show yet, then
                        follows the reveal. */}
                    <MapLoadingPlaceholder
                      show={!canShowMap || showPlaceholder}
                    />
                    {canShowMap && (
                      <div
                        data-testid="property-create-map"
                        className={`h-full ${mapRevealClassName}`}
                      >
                        <PropertyMapWrapper
                          onFit={revealMap}
                          shapeIdSelected={shapeIds}
                          geoJSONCollection={geoJSONCollection}
                          onFeaturesChange={handleFeaturesChange}
                          onSelectFeatures={handleFeaturesSelect}
                          liveUpdate={false}
                          zoom={fieldZoom}
                          setZoom={setZoom}
                          isCreate={!fieldId}
                          currentCoordinates={fieldCentroidCoordinates}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
          }}
        </Formik>
        {isToastVisible && (
          <PropertyDetailsToast
            state="error"
            selectedProperty={{ type: fieldToEdit?.$landType }}
            showEditToast={isToastVisible}
            setShowEditToast={setIsToastVisible}
          />
        )}
      </div>
    </>
  );
};

// "Save and draw again" replaces the location to start the next field; keying
// on it gives that field a fresh form and map.
const PropertyCreate = () => {
  const { key } = useLocation();
  return <PropertyCreateScreen key={key} />;
};

export default PropertyCreate;
